import { Trans } from '@lingui/react/macro';

import type {
  HealthCheckFieldAccessErrorType,
  IntegrationConnectionHealthCheckType,
  IntegrationProviderType,
} from '@/api/integration';
import { getProviderHelpLinkById } from '@/app/integrations/providerUtil';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { Link } from '@/components/typography';

import HealthCheckAPIAccessError from './HealthCheckAPIAccessError';
import HealthCheckDefaultError from './HealthCheckDefaultError';
import HealthCheckFieldAccessError from './HealthCheckFieldAccessError';

type Props = {
  provider: IntegrationProviderType;
  healthChecks: IntegrationConnectionHealthCheckType[];
  open: boolean;
  onClose: () => void;
};

const HealthCheckDialog = ({ provider, healthChecks, open, onClose }: Props) => {
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Health Check Errors</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          {healthChecks.map((healthCheck) => {
            switch (healthCheck.type) {
              case 'field-access-error':
                return (
                  <HealthCheckFieldAccessError
                    key={healthCheck.type}
                    healthCheck={healthCheck as HealthCheckFieldAccessErrorType}
                  />
                );
              case 'api-access-error':
                return (
                  <HealthCheckAPIAccessError key={healthCheck.type} providerName={provider.name} />
                );
              default:
                return <HealthCheckDefaultError key={healthCheck.type} healthCheck={healthCheck} />;
            }
          })}
          <Trans>
            For more details please review our{' '}
            <Link
              to={getProviderHelpLinkById(provider.id)}
              variant="body1"
              target="_blank"
              showExternalIcon
            >
              Support Center
            </Link>
          </Trans>
        </div>
        <DialogFooter>
          <DialogClose
            render={
              <Button variant="on-primary" size="large" autoFocus>
                <Trans>Ok</Trans>
              </Button>
            }
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HealthCheckDialog;
